import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Phone, UtensilsCrossed } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { trackWhatsAppClick } from '../../utils/analytics';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Restaurant = () => {
  const [content, setContent] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);
  const menuContainerRef = useRef(null);
  const [navBtnTop, setNavBtnTop] = useState('50%');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/content/restaurant`);
        const contentMap = {};
        const menus = [];
        response.data.forEach(item => {
          if (item.section.startsWith('menu_item_')) {
            menus.push({ ...item.content, section: item.section });
          } else {
            contentMap[item.section] = item.content;
          }
        });
        setContent(contentMap);
        // Sort menu items by order
        menus.sort((a, b) => (a.order || 0) - (b.order || 0));
        if (menus.length > 0) {
          setMenuItems(menus);
        }
      } catch (error) {
        console.error('Error fetching restaurant content:', error);
      }
    };
    fetchContent();
  }, []);

  // Hero content with multiple slides
  const heroSlides = [];
  const heroMain = content.hero;
  if (heroMain && heroMain.image) {
    heroSlides.push(heroMain);
  }
  if (content.hero_slide_2 && content.hero_slide_2.image) {
    heroSlides.push(content.hero_slide_2);
  }
  if (content.hero_slide_3 && content.hero_slide_3.image) {
    heroSlides.push(content.hero_slide_3);
  }

  // Default hero slides if none from API
  const defaultSlides = [
    {
      title: 'Kenari Restaurant',
      subtitle: 'Culinary Experience',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200',
    },
    {
      title: 'Authentic Indonesian Cuisine',
      subtitle: 'Fresh & Local Ingredients',
      image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200',
    },
    {
      title: 'Mountain View Dining',
      subtitle: 'A Taste of Nature',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200',
    },
  ];

  const slides = heroSlides.length > 0 ? heroSlides : defaultSlides;

  // Auto-play hero slider
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Compute nav button vertical position: center of visible portion of menu image
  useEffect(() => {
    const updateNavPosition = () => {
      const el = menuContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Visible portion of the container within the viewport
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, vh);
      if (visibleBottom <= visibleTop) {
        // Not visible at all, default to center
        setNavBtnTop('50%');
        return;
      }
      // Center of visible area relative to the container
      const centerInViewport = (visibleTop + visibleBottom) / 2;
      const centerInContainer = centerInViewport - rect.top;
      // Clamp within container bounds with some padding
      const clamped = Math.max(40, Math.min(centerInContainer, rect.height - 40));
      setNavBtnTop(`${clamped}px`);
    };

    updateNavPosition();
    window.addEventListener('scroll', updateNavPosition, { passive: true });
    window.addEventListener('resize', updateNavPosition, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateNavPosition);
      window.removeEventListener('resize', updateNavPosition);
    };
  }, []);

  // Default menu images
  const defaultMenuItems = [
    { url: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800', caption: 'Menu Halaman 1', order: 0 },
    { url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800', caption: 'Menu Halaman 2', order: 1 },
    { url: 'https://images.unsplash.com/photo-1482049016688-2d3e1b311543?w=800', caption: 'Menu Halaman 3', order: 2 },
    { url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800', caption: 'Menu Halaman 4', order: 3 },
  ];

  const displayMenuItems = menuItems.length > 0 ? menuItems : defaultMenuItems;

  // Preload images for smooth transitions
  useEffect(() => {
    displayMenuItems.forEach((item) => {
      if (item.url) {
        const img = new Image();
        img.src = item.url;
      }
    });
  }, [displayMenuItems]);

  // CTA content
  const ctaContent = content.cta || {};

  // Page flip navigation
  const totalPages = displayMenuItems.length;

  const goToNextPage = useCallback(() => {
    if (currentPage >= totalPages - 1) return;
    setCurrentPage((prev) => prev + 1);
  }, [currentPage, totalPages]);

  const goToPrevPage = useCallback(() => {
    if (currentPage <= 0) return;
    setCurrentPage((prev) => prev - 1);
  }, [currentPage]);

  // Swipe / drag handler
  const swipeThreshold = 50;
  const handleDragEnd = useCallback((_event, info) => {
    if (Math.abs(info.offset.x) > swipeThreshold) {
      if (info.offset.x < 0) {
        goToNextPage();
      } else {
        goToPrevPage();
      }
    }
  }, [goToNextPage, goToPrevPage]);

  // Smooth crossfade variants
  const fadeVariants = {
    enter: {
      opacity: 0,
    },
    center: {
      opacity: 1,
      transition: {
        opacity: { duration: 0.3 },
      },
    },
    exit: {
      opacity: 0,
      transition: {
        opacity: { duration: 0.3 },
      },
    },
  };

  return (
    <div className="bg-emerald-50/30" data-testid="restaurant-page">
      {/* SEO H1 */}
      <h1 className="sr-only">Kenari Restaurant Spencer Green Hotel Batu | Menu & Dining Experience</h1>

      {/* ==================== HERO SLIDER ==================== */}
      <section className="relative h-[100svh] min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Slides */}
        <AnimatePresence mode="wait">
          <motion.div
            key={heroIndex}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slides[heroIndex]?.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Hero Content */}
        <div className="relative text-center px-4 z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={heroIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              <p className="hero-subtitle text-emerald-300 uppercase tracking-widest text-sm mb-3">
                {slides[heroIndex]?.subtitle || 'Culinary Experience'}
              </p>
              <p className="font-display hero-title text-5xl md:text-6xl text-white mb-4" role="heading" aria-level="2">
                {slides[heroIndex]?.title || 'Kenari Restaurant'}
              </p>
              {slides[heroIndex]?.description && (
                <p className="hero-subtitle text-emerald-100 max-w-2xl mx-auto text-lg">
                  {slides[heroIndex].description}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide indicators */}
        {slides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setHeroIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === heroIndex
                    ? 'bg-emerald-400 w-8'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                data-testid={`hero-slide-dot-${idx}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* ==================== MENU SECTION ==================== */}
      <section className="bg-white" data-testid="menu-section">
        {/* Section Header */}
        <div className="text-center py-10 sm:py-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-12 h-[1px] bg-emerald-600" />
              <UtensilsCrossed className="w-5 h-5 text-emerald-600" />
              <div className="w-12 h-[1px] bg-emerald-600" />
            </div>
            <p className="text-emerald-600 uppercase tracking-widest text-sm mb-2">Our Menu</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-gray-900">
              Menu Kenari Restaurant
            </h2>
          </motion.div>
        </div>

        {/* Menu Image – 80% Width, Nav Buttons Overlay on Image */}
        <div ref={menuContainerRef} className="relative w-[90%] sm:w-[80%] max-w-5xl mx-auto" data-testid="menu-book">
          
          {/* Menu image with swipe support */}
          <div
            className="w-full relative overflow-hidden select-none"
            style={{ touchAction: 'pan-y' }}
          >
            <motion.div
              className="flex cursor-grab active:cursor-grabbing"
              animate={{ x: `-${currentPage * 100}%` }}
              transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={handleDragEnd}
            >
              {displayMenuItems.map((item, idx) => (
                <div key={idx} className="w-full flex-shrink-0 px-0.5">
                  <Zoom>
                    <img
                      src={item.url}
                      alt={item.caption || `Menu Halaman ${idx + 1}`}
                      className="w-full h-auto object-contain rounded-lg"
                      draggable={false}
                      data-testid={`menu-page-${idx}`}
                    />
                  </Zoom>
                </div>
              ))}
            </motion.div>

            {/* Transparent minimalist nav button – LEFT */}
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              className={`absolute left-2 sm:left-4 z-20 -translate-y-1/2 flex items-center justify-center transition-all duration-300 group ${
                currentPage === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
              style={{ top: navBtnTop }}
              data-testid="menu-prev-btn"
              aria-label="Menu sebelumnya"
            >
              <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:bg-black/40 group-hover:scale-110 group-active:scale-95 shadow-lg">
                <ChevronLeft className="w-5 h-5 text-white drop-shadow-md" />
              </span>
            </button>

            {/* Transparent minimalist nav button – RIGHT */}
            <button
              onClick={goToNextPage}
              disabled={currentPage >= totalPages - 1}
              className={`absolute right-2 sm:right-4 z-20 -translate-y-1/2 flex items-center justify-center transition-all duration-300 group ${
                currentPage >= totalPages - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
              style={{ top: navBtnTop }}
              data-testid="menu-next-btn"
              aria-label="Menu selanjutnya"
            >
              <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:bg-black/40 group-hover:scale-110 group-active:scale-95 shadow-lg">
                <ChevronRight className="w-5 h-5 text-white drop-shadow-md" />
              </span>
            </button>
          </div>
        </div>

        {/* Page dots only – no text */}
        <div className="flex items-center justify-center py-5">
          <div className="flex gap-1.5">
            {displayMenuItems.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (idx === currentPage) return;
                  setCurrentPage(idx);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentPage
                    ? 'bg-emerald-600 w-6'
                    : 'bg-emerald-300/50 hover:bg-emerald-400'
                }`}
                data-testid={`menu-page-dot-${idx}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA SECTION ==================== */}
      <section className="py-20 bg-emerald-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl font-medium text-white mb-4">
              {ctaContent.title || 'Reservasi Meja Kenari Restaurant'}
            </h2>
            <p className="text-emerald-200 mb-8 max-w-xl mx-auto">
              {ctaContent.description || 'Nikmati pengalaman kuliner terbaik di Spencer Green Hotel. Hubungi kami untuk reservasi meja atau pertanyaan seputar menu.'}
            </p>
            <a
              href="https://wa.me/6281130700206?text=Hi,%20saya%20ingin%20reservasi%20meja%20di%20Kenari%20Restaurant%20Spencer%20Green%20Hotel"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-emerald-800 px-8 py-4 rounded-full font-semibold hover:bg-emerald-50 transition-colors shadow-lg hover:shadow-xl"
              onClick={() => trackWhatsAppClick('Restaurant Page CTA')}
              data-testid="restaurant-contact-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Reservasi via WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Restaurant;
